/* eslint-disable react-hooks/exhaustive-deps */

import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {Loader, Modal, PrimaryButton} from '../../components';
import {closeModal} from '../../assets/images/images';
import {useSelector} from 'react-redux';
import {useMutation, useQueryClient} from 'react-query';
import PayStack from '@paystack/inline-js';
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';

import {initializePayment, validatePayment} from '../../requests/queries/flows';
import {toast} from 'react-toastify';
import {stripePromise} from '../../helpers/constants/stripeConstants';
import {useProfiledRiskPaymentModal} from '../FraudDetection/Flows/hooks/useProfiledRiskPaymentModal';
import PermissionsModal from './components/PemissionModal';

export const ProfiledRiskPaymentModal = memo(() => {
  const {userDetails} = useSelector(state => state?.auth);
  const [paymentCode, setPaymentCode] = useState('');
  const [stripeClientSecret, setStripeClientSecret] = useState('');
  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const route = location.pathname;

  const queryClient = useQueryClient();

  const {
    billingStatus,
    trialStatus,
    isLoading: checksLoading,
    closePaymentPopup,
    showPaymentPopup,
    setShowPaymentPopup,
    companyData,
  } = useProfiledRiskPaymentModal();
  useEffect(() => {
    if (
      !billingStatus &&
      trialStatus &&
      !route?.includes('/profiled-risk-transactions')
    ) {
      setSubscriptionOpen(true);
    }
  }, [billingStatus, route, trialStatus]);

  const companyCurrency = companyData?.currency?.toUpperCase();
  const shouldUseStripe = companyCurrency && companyCurrency !== 'NGN';

  const {mutate: startPayment, isLoading} = useMutation(
    'payment-init',
    initializePayment,
    {
      onSuccess: data => {
        const authCode = data?.auth_code;
        if (!authCode) {
          toast.error('Unable to initiate payment. Please try again.');
          return;
        }

        if (shouldUseStripe) {
          setStripeClientSecret(authCode);
          setIsStripeModalOpen(true);
          // closePaymentPopup();
          return;
        }

        setPaymentCode(authCode);
        closePaymentPopup();
        queryClient.invalidateQueries({queryKey: ['pr-company-details']});
      },
      onError: error => {
        toast.error(error.response.data.error || 'Failed to initiate payment');
      },
    },
  );
  const {mutate: validatePaymentMutate, isLoading: validateLoading} =
    useMutation({
      mutationFn: validatePayment,
      onSuccess: () => {
        closePaymentPopup();
        queryClient.invalidateQueries({queryKey: ['pr-company-details']});
      },
      onError: error => {
        toast.error(
          error.response.data.error ||
            'An error occurred during the payment process',
        );
      },
    });

  useEffect(() => {
    if (paymentCode && !shouldUseStripe) {
      // closePaymentPopup();
      const popup = new PayStack();
      popup.resumeTransaction(paymentCode, {
        onSuccess: function () {
          validatePaymentMutate({
            access_code: paymentCode,
          });
        },
        onCancel: function () {
          toast.error('Payment was not completed');
          setShowPaymentPopup(!billingStatus);
        },
        onError: function (error) {
          toast.error('An error occurred during the payment process');
          console.error('Payment error:', error);
          setShowPaymentPopup(!billingStatus);
        },
      });
    }
  }, [validatePaymentMutate, paymentCode]);

  const handleStripeModalClose = useCallback(() => {
    setIsStripeModalOpen(false);
    setStripeClientSecret('');
    if (!billingStatus) {
      setShowPaymentPopup(true);
      toast.error('Payment was not completed');
    }
  }, [billingStatus, setShowPaymentPopup]);

  const handleStripeCheckoutComplete = useCallback(() => {
    if (!stripeClientSecret) return;
    validatePaymentMutate(
      {
        access_code: stripeClientSecret,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ['pr-company-details']});
          handleStripeModalClose();
        },
      },
    );
  }, [
    handleStripeModalClose,
    queryClient,
    stripeClientSecret,
    validatePaymentMutate,
  ]);

  const handleSubmit = useCallback(() => {
    if (!userDetails?.company?.id) {
      return toast.error('Business ID not found. Please try again.');
    }

    startPayment({
      company_id: userDetails?.company?.id,
    });
  }, [startPayment, userDetails?.company?.id]);

  const handleBackdropClick = e => {
    if (e.target === e.currentTarget && billingStatus) {
      closePaymentPopup();
    }
  };

  if (subscriptionOpen) {
    return (
      <PermissionsModal
        open={subscriptionOpen}
        showCancelBtn={false}
        title="Subscription Expired"
        buttonText="Upgrade to Continue"
        setOpen={setSubscriptionOpen}
        body={
          <p className="text-body text-sm leading-6">
            Your subscription has expired. Please upgrade to
            <b> Growth, Premium, or Enterprise plan</b> to continue using the
            platform.
          </p>
        }
      />
    );
  }
  if (!showPaymentPopup || checksLoading) return null;

  function StripeEmbeddedCheckout({clientSecret, onComplete}) {
    const checkoutOptions = useMemo(
      () => ({
        clientSecret,
        onComplete,
      }),
      [clientSecret, onComplete],
    );

    if (!clientSecret) return null;

    return (
      <div className="stripe-embedded-checkout">
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={checkoutOptions}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    );
  }

  if (shouldUseStripe && isStripeModalOpen && stripeClientSecret) {
    return (
      <Modal
        show={isStripeModalOpen}
        onClose={handleStripeModalClose}
        extraModalClasses="max-w-[528px] overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal"
        modalPosition="justify-center items-center"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-medium text-deepBlue">
              Stripe Payment
            </h4>
            <button
              onClick={handleStripeModalClose}
              className="text-sm text-body outline-0"
            >
              <img src={closeModal} alt="close icon" />
            </button>
          </div>
          {checksLoading || isLoading ? (
            <Loader height={25} />
          ) : (
            <StripeEmbeddedCheckout
              clientSecret={stripeClientSecret}
              onComplete={handleStripeCheckoutComplete}
            />
          )}
        </div>
      </Modal>
    );
  }

  return createPortal(
    <div
      className="fixed font-sans inset-0 bg-black bg-opacity-70 backdrop-blur-[16px] flex justify-center items-center z-[999]"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl  max-w-[420px] w-full">
        <div className="flex flex-col items-center text-start">
          <div className="flex p-5 py-4 items-center justify-between w-full border-b border-brandBlue4">
            <h2 className="text-base font-medium ">Activate Free Trial</h2>
          </div>
          <ul className="list-disc space-y-2 ml-8 mr-6 my-4">
            <li className="text-tiny text-[#344272]">
              To start using ProfiledRisk, you need to have an active
              subscription.
            </li>
            <li className="text-tiny text-[#344272]">
              We are offering you a 14 day risk free trial to enjoy our rich
              features.
            </li>
          </ul>

          <p className="text-[#727272] mb-2 text-small mr-auto ml-5">
            A ₦100 processing fee would be charged on your card
          </p>

          <button className="underline mb-2 underline-offset-4 text-small text-brandBlue ml-5 mr-auto">
            Simply activate the trial to get started.
          </button>

          <div className="flex p-5 py-4 items-center justify-center gap-4 w-full border-t border-brandBlue4">
            {/* <SecondaryButton
              buttonText="Cancel"
              className="lg:min-w-[180px] border !h-[40px] !border-[#dfe4eb] !outline-none"
              onClick={() => billingStatus && closePaymentPopup()}
            /> */}

            <PrimaryButton
              buttonText="Activate Free Plan"
              className="lg:min-w-[180px] w-full !h-[40px]"
              onClick={handleSubmit}
              loading={isLoading || validateLoading}
              disabled={isLoading || validateLoading}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
});
